"use client";

/**
 * Attaches an adaptive HLS source to the player's <video> and exposes the
 * available quality levels.
 *
 * For a plain MP4 source this hook does nothing at all and reports no levels,
 * which is what keeps every existing clip working unchanged — the quality menu
 * simply stays hidden. hls.js is loaded through a dynamic import so its ~400 KB
 * never reach a page that only ever plays MP4.
 *
 * Where the source is played:
 *  - MSE available (all desktop browsers, Chrome/Firefox/Safari) → hls.js, which
 *    is what gives us a level list and therefore a quality menu at all.
 *  - No MSE but native HLS (iOS Safari) → hand the playlist to the element and
 *    let the OS adapt. No menu there; iOS exposes no level API.
 */

import type HlsJs from "hls.js";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ClipQualityLevel, ClipQualityState } from "./clip-quality-levels";
import {
  AUTO_QUALITY_INDEX,
  isHlsSource,
  toQualityLevels,
} from "./clip-quality-levels";

/**
 * ABR tuning — deliberately optimistic.
 *
 * hls.js ships conservative defaults built for mobile networks: it assumes
 * 0.5 Mbit/s until measured otherwise, so the first seconds of every clip play
 * at the worst rung and only crawl upwards. For screen recordings that is the
 * wrong trade — these clips get embedded on the website, where a soft first
 * impression is worse than a brief risk of rebuffering. So: start from a high
 * estimate, climb back up eagerly, and only drop when the buffer really runs dry.
 */
export const CLIP_ABR_CONFIG = {
  /**
   * Bandwidth assumed before anything has been measured. hls.js defaults to
   * 500_000; at 5 Mbit/s the top rung is chosen immediately and a genuinely slow
   * line is corrected within the first segment or two.
   */
  abrEwmaDefaultEstimate: 5_000_000,
  /** Fraction of measured bandwidth needed to move up (default 0.7 — slower). */
  abrBandWidthUpFactor: 0.85,
  /** Safety margin for staying put. hls.js default; lowering invites flapping. */
  abrBandWidthFactor: 0.95,
  /**
   * Off on purpose: a clip embedded in a small box on the website should still
   * be sharp. Capping to the player's pixel size is the single most "cautious"
   * default hls.js has.
   */
  capLevelToPlayerSize: false,
  /** -1 = let ABR pick, using the optimistic estimate above. */
  startLevel: -1,
  /** Seconds of lookahead. More headroom over a jittery long-haul connection. */
  maxBufferLength: 40,
  /** Drop played-out buffer to keep memory flat on long clips. */
  backBufferLength: 30,
  /** VOD, never live. */
  lowLatencyMode: false,
  enableWorker: true,
} as const;

/** Fatal-error recovery attempts before giving up and asking for a fresh URL. */
const MAX_RECOVERIES = 2;

export interface ClipHlsResult {
  readonly quality: ClipQualityState;
  /**
   * What `<video src>` should carry. `undefined` while hls.js drives the
   * element — setting src there would make the browser fetch the playlist
   * natively in parallel.
   */
  readonly videoSrc: string | undefined;
}

export function useClipHls(
  videoRef: RefObject<HTMLVideoElement | null>,
  options: {
    readonly src: string;
    readonly onError?: () => void;
  },
): ClipHlsResult {
  const hlsSource = isHlsSource(options.src);
  const hlsRef = useRef<HlsJs | null>(null);
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;

  const [levels, setLevels] = useState<readonly ClipQualityLevel[]>([]);
  const [selectedIndex, setSelected] = useState<number>(AUTO_QUALITY_INDEX);
  const [activeIndex, setActiveIndex] = useState<number>(AUTO_QUALITY_INDEX);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsSource) {
      setLevels([]);
      setSelected(AUTO_QUALITY_INDEX);
      setActiveIndex(AUTO_QUALITY_INDEX);
      return;
    }

    // Guards the async import: a src change (URL refresh, next clip) must not
    // let a late-resolving module attach to an element that moved on.
    let cancelled = false;
    let recoveries = 0;
    const src = options.src;

    void (async () => {
      const { default: Hls } = await import("hls.js");
      if (cancelled) return;

      if (!Hls.isSupported()) {
        // iOS Safari: no MSE, but the OS plays HLS and adapts on its own.
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.crossOrigin = "use-credentials";
          video.src = src;
        } else {
          onErrorRef.current?.();
        }
        return;
      }

      const hls = new Hls({
        ...CLIP_ABR_CONFIG,
        // Signed CloudFront cookies live on the parent domain; without this the
        // playlist and every segment request would go out unauthenticated.
        xhrSetup: (xhr: XMLHttpRequest) => {
          xhr.withCredentials = true;
        },
        fetchSetup: (context: { url: string }, initParams: RequestInit) =>
          new Request(context.url, { ...initParams, credentials: "include" }),
      });
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        setLevels(toQualityLevels(hls.levels));
        setActiveIndex(hls.currentLevel);
      });
      hls.on(Hls.Events.LEVEL_SWITCHED, (_evt, data) => {
        if (cancelled) return;
        setActiveIndex(data.level);
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (cancelled || !data.fatal) return;
        const status = data.response?.code;
        // 401/403 means the signed cookies expired — retrying the same request
        // loops forever. Hand back to the caller, which re-mints access and
        // restores currentTime (see clip-share-player).
        const expired = status === 401 || status === 403;
        if (!expired && recoveries < MAX_RECOVERIES) {
          recoveries += 1;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
        }
        onErrorRef.current?.();
      });

      hls.loadSource(src);
      hls.attachMedia(video);
    })();

    return () => {
      cancelled = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [options.src, hlsSource, videoRef]);

  const setSelectedIndex = useCallback((index: number): void => {
    setSelected(index);
    const hls = hlsRef.current;
    if (!hls) return;
    // `currentLevel` switches straight away (flushing what is buffered) rather
    // than at the next segment boundary — a viewer who picks 1080p expects to
    // see it now, not in ten seconds.
    hls.currentLevel = index;
  }, []);

  return {
    quality: { levels, selectedIndex, activeIndex, setSelectedIndex },
    videoSrc: hlsSource ? undefined : options.src,
  };
}
